import { profileClient } from "@/shared/api/clients";
import {
  ListUsersResponse,
  StationSeats,
  StationSeatsResponse,
} from "../interfaces/ListUsersResponse";
import { ReportGenUser } from "../interfaces/User";

export type { StationSeats };

/**
 * ListUsersResponse normalized for the app: users enriched from metadata and
 * roster completeness read from the X-Lookup-Failures header — deliberately
 * NOT the same type as the wire contract.
 */
export interface StationRoster {
  users: ReportGenUser[];
  /** Members omitted from `users` because their identity lookup failed. */
  lookupFailures: number;
}

export async function fetchStationRoster(
  organizationId: string,
  includeInactive = false
): Promise<StationRoster> {
  const response = await profileClient.get<ListUsersResponse>(
    `/v1/stations/${organizationId}/users`,
    { params: { includeInactive } }
  );
  return {
    users: response.data.users.map((user) => ({
      ...user,
      rank: user.metadata.position || "",
      badgeNumber: user.metadata.badge_number || "",
      station: user.metadata.station || "",
      status: user.membershipStatus,
      rmsUserId: user.metadata.rmsUserId || null,
      bodycamUserId: user.metadata.bodycamUserId || null,
      department: user.metadata.department || "",
      role: user.role ?? null,
    })),
    lookupFailures: parseLookupFailures(response.headers),
  };
}

function parseLookupFailures(headers: Record<string, unknown>): number {
  // Axios lowercases response header names.
  const parsed = Number(headers?.["x-lookup-failures"]);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

/** Seat usage for the station; null when no seat limit is configured. */
export async function fetchStationSeats(
  organizationId: string,
): Promise<StationSeats | null> {
  const response = await profileClient.get<StationSeatsResponse>(
    `/v1/stations/${organizationId}/seats`,
  );
  return response.data.seats ?? null;
}
